import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { AgFilterComponent } from '../../../../global/gx-ag-grid/ag-filter.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BusinessStatusListEnum } from '../../../enums/business-status-list-enum';
import { ManagementStatusListEnum } from '../../../enums/management-status-list-enum';
import { StatusFilterEnum } from '../../../enums/status-filter-and-counters/status-filter-enum';
import { IDoesFilterPassParams } from 'ag-grid-community';
import { hasBlacklistedFields, hasDifferences } from '../../../location-utils';
import { castAsFormControl, getEnumValues } from '../../../../../core/utilities';
import { takeUntil } from 'rxjs/operators';
import { Location } from '../../../../../models/locations-models/location';
import { MatButtonToggleGroup, MatButtonToggle } from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
    selector: 'app-status-filter',
    templateUrl: './status-filter.component.html',
    styleUrls: ['../../../../../../assets/scss/partials/status-filter-icon-styling.scss',
                './status-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, MatButton, MatIcon, MatButtonToggleGroup, MatButtonToggle]
})
export class StatusFilterComponent extends AgFilterComponent {

  // Template use
  StatusFilterEnum = StatusFilterEnum;
  BusinessStatusListEnum = BusinessStatusListEnum;
  ManagementStatusListEnum = ManagementStatusListEnum;
  castAsFormControl = castAsFormControl;

  // Hack to enable 'turning off' toggle group buttons in 'single' mode
  // depends on mat-button-toggle-group (change) event firing before (click) event
  singleToggleGrpValueChanges = {
    [StatusFilterEnum.HAS_BLACKLISTED_FIELDS]: false,
    [StatusFilterEnum.HAS_IGNORED_DIFFERENCES]: false,
    [StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS]: false,
    [StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS]: false,
    [StatusFilterEnum.HAS_COMPLETED_CC]: false,
    [StatusFilterEnum.HAS_UPDATE_ERROR]: false,
    [StatusFilterEnum.HAS_IMPORT_ERROR]: false,
    [StatusFilterEnum.IS_VERIFIED]: false,
  };

  filterState: FormGroup<{
    [StatusFilterEnum.BUSINESS_STATUS_LIST]: FormControl<BusinessStatusListEnum[]>,
    [StatusFilterEnum.MANAGEMENT_STATUS_LIST]: FormControl<ManagementStatusListEnum[]>,
    [StatusFilterEnum.HAS_BLACKLISTED_FIELDS]: FormControl<boolean>,
    [StatusFilterEnum.HAS_IGNORED_DIFFERENCES]: FormControl<boolean>,
    [StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS]: FormControl<boolean>,
    [StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS]: FormControl<boolean>,
    [StatusFilterEnum.HAS_COMPLETED_CC]: FormControl<boolean>,
    [StatusFilterEnum.HAS_UPDATE_ERROR]: FormControl<boolean>,
    [StatusFilterEnum.HAS_IMPORT_ERROR]: FormControl<boolean>,
    [StatusFilterEnum.IS_VERIFIED]: FormControl<boolean>,
  }> = new FormGroup({
    [StatusFilterEnum.BUSINESS_STATUS_LIST]: new FormControl([]),
    [StatusFilterEnum.MANAGEMENT_STATUS_LIST]: new FormControl([]),
    [StatusFilterEnum.HAS_BLACKLISTED_FIELDS]: new FormControl(null),
    [StatusFilterEnum.HAS_IGNORED_DIFFERENCES]: new FormControl(null),
    [StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS]: new FormControl(null),
    [StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS]: new FormControl(null),
    [StatusFilterEnum.HAS_COMPLETED_CC]: new FormControl(null),
    [StatusFilterEnum.HAS_UPDATE_ERROR]: new FormControl(null),
    [StatusFilterEnum.HAS_IMPORT_ERROR]: new FormControl(null),
    [StatusFilterEnum.IS_VERIFIED]: new FormControl(null),
  });

  constructor(private cd: ChangeDetectorRef) {
    super();

    this.filterState.valueChanges.pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.updateFilter();
        });
  }

  override get filterValue(): string[] {
    let filterValue: string[] = [];

    filterValue = [...filterValue,
      ...this.filterState.get(StatusFilterEnum.BUSINESS_STATUS_LIST).value,
      ...this.filterState.get(StatusFilterEnum.MANAGEMENT_STATUS_LIST).value];

    if (this.filterState.get(StatusFilterEnum.HAS_BLACKLISTED_FIELDS).value !== null) {
      if (this.filterState.get(StatusFilterEnum.HAS_BLACKLISTED_FIELDS).value) {
        filterValue.push(StatusFilterEnum.HAS_BLACKLISTED_FIELDS);
      } else filterValue.push(StatusFilterEnum.HAS_NO_BLACKLISTED_FIELDS);
    }

    if (this.filterState.get(StatusFilterEnum.HAS_IGNORED_DIFFERENCES).value !== null) {
      if (this.filterState.get(StatusFilterEnum.HAS_IGNORED_DIFFERENCES).value) {
        filterValue.push(StatusFilterEnum.HAS_IGNORED_DIFFERENCES);
      } else filterValue.push(StatusFilterEnum.HAS_NO_IGNORED_DIFFERENCES);
    }

    if (this.filterState.get(StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS).value !== null) {
      if (this.filterState.get(StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS).value) {
        filterValue.push(StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS);
      } else filterValue.push(StatusFilterEnum.IS_NOT_DIFFERENT_IN_MY_BUSINESS);
    }

    if (this.filterState.get(StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS).value !== null) {
      if (this.filterState.get(StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS).value) {
        filterValue.push(StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS);
      } else filterValue.push(StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS);
    }

    if (this.filterState.get(StatusFilterEnum.HAS_COMPLETED_CC).value !== null) {
      if (this.filterState.get(StatusFilterEnum.HAS_COMPLETED_CC).value) {
        filterValue.push(StatusFilterEnum.HAS_COMPLETED_CC);
      } else filterValue.push(StatusFilterEnum.HAS_NOT_COMPLETED_CC);
    }

    if (this.filterState.get(StatusFilterEnum.HAS_UPDATE_ERROR).value !== null) {
      if (this.filterState.get(StatusFilterEnum.HAS_UPDATE_ERROR).value) {
        filterValue.push(StatusFilterEnum.HAS_UPDATE_ERROR);
      } else filterValue.push(StatusFilterEnum.HAS_NO_UPDATE_ERROR);
    }

    if (this.filterState.get(StatusFilterEnum.HAS_IMPORT_ERROR).value !== null) {
      if (this.filterState.get(StatusFilterEnum.HAS_IMPORT_ERROR).value) {
        filterValue.push(StatusFilterEnum.HAS_IMPORT_ERROR);
      } else filterValue.push(StatusFilterEnum.HAS_NO_IMPORT_ERROR);
    }

    if (this.filterState.get(StatusFilterEnum.IS_VERIFIED).value !== null) {
      if (this.filterState.get(StatusFilterEnum.IS_VERIFIED).value) {
        filterValue.push(StatusFilterEnum.IS_VERIFIED);
      } else filterValue.push(StatusFilterEnum.IS_NOT_VERIFIED);
    }

    return filterValue;
  }

  /**
   * A 'single' toggleButtonGroup behaves like a radio group (value cannot be unselected).
   * Reset the respective FC value if (change) event did not fire.
   * @param statusFilter
   */
  resetSingleToggleGrp(statusFilter: StatusFilterEnum) {
    const fc = this.filterState.get(statusFilter);
    if (!this.singleToggleGrpValueChanges[statusFilter]) {
      fc.reset(null, {emitEvent: true});
    }
    this.singleToggleGrpValueChanges[statusFilter] = false;
  }

  private resetFormGroup() {
    this.filterState.reset({
      [StatusFilterEnum.BUSINESS_STATUS_LIST]: [],
      [StatusFilterEnum.MANAGEMENT_STATUS_LIST]: [],
    }, { emitEvent: false });
  }

  /**
   * Track button toggle group value change
   * @param statusFilter
   */
  onSingleToggleGrpChange(statusFilter: StatusFilterEnum) {
    this.singleToggleGrpValueChanges[statusFilter] = true;
  }

  override setModel(model: string[]): void {
    this.resetFormGroup();

    model?.forEach((item) => {
      switch(item) {
        case BusinessStatusListEnum.OPEN:
        case BusinessStatusListEnum.CLOSED_PERMANENTLY:
        case BusinessStatusListEnum.CLOSED_TEMPORARILY:
          this.filterState.get(StatusFilterEnum.BUSINESS_STATUS_LIST).setValue(
              [...this.filterState.get(StatusFilterEnum.BUSINESS_STATUS_LIST).value, item]);
          break;
        case ManagementStatusListEnum.AUTOMATICALLY_MANAGED:
        case ManagementStatusListEnum.MANUALLY_MANAGED:
        case ManagementStatusListEnum.NOT_MANAGED:
          this.filterState.get(StatusFilterEnum.MANAGEMENT_STATUS_LIST).setValue(
              [...this.filterState.get(StatusFilterEnum.MANAGEMENT_STATUS_LIST).value, item])
          break;
        case StatusFilterEnum.HAS_BLACKLISTED_FIELDS:
          this.filterState.get(StatusFilterEnum.HAS_BLACKLISTED_FIELDS).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_BLACKLISTED_FIELDS] = true;
          break;
        case StatusFilterEnum.HAS_NO_BLACKLISTED_FIELDS:
          this.filterState.get(StatusFilterEnum.HAS_BLACKLISTED_FIELDS).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_BLACKLISTED_FIELDS] = true;
          break;
        case StatusFilterEnum.HAS_IGNORED_DIFFERENCES:
          this.filterState.get(StatusFilterEnum.HAS_IGNORED_DIFFERENCES).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_IGNORED_DIFFERENCES] = true;
          break;
        case StatusFilterEnum.HAS_NO_IGNORED_DIFFERENCES:
          this.filterState.get(StatusFilterEnum.HAS_IGNORED_DIFFERENCES).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_IGNORED_DIFFERENCES] = true;
          break;
        case StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS:
          this.filterState.get(StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS] = true;
          break;
        case StatusFilterEnum.IS_NOT_DIFFERENT_IN_MY_BUSINESS:
          this.filterState.get(StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS] = true;
          break;
        case StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS:
          this.filterState.get(StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS] = true;
          break;
        case StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS:
          this.filterState.get(StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS] = true;
          break;
        case StatusFilterEnum.HAS_COMPLETED_CC:
          this.filterState.get(StatusFilterEnum.HAS_COMPLETED_CC).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_COMPLETED_CC] = true;
          break;
        case StatusFilterEnum.HAS_NOT_COMPLETED_CC:
          this.filterState.get(StatusFilterEnum.HAS_COMPLETED_CC).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_COMPLETED_CC] = true;
          break;
        case StatusFilterEnum.HAS_UPDATE_ERROR:
          this.filterState.get(StatusFilterEnum.HAS_UPDATE_ERROR).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_UPDATE_ERROR] = true;
          break;
        case StatusFilterEnum.HAS_NO_UPDATE_ERROR:
          this.filterState.get(StatusFilterEnum.HAS_UPDATE_ERROR).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_UPDATE_ERROR] = true;
          break;
        case StatusFilterEnum.HAS_IMPORT_ERROR:
          this.filterState.get(StatusFilterEnum.HAS_IMPORT_ERROR).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_IMPORT_ERROR] = true;
          break;
        case StatusFilterEnum.HAS_NO_IMPORT_ERROR:
          this.filterState.get(StatusFilterEnum.HAS_IMPORT_ERROR).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.HAS_IMPORT_ERROR] = true;
          break;
        case StatusFilterEnum.IS_VERIFIED:
          this.filterState.get(StatusFilterEnum.IS_VERIFIED).setValue(true, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_VERIFIED] = true;
          break;
        case StatusFilterEnum.IS_NOT_VERIFIED:
          this.filterState.get(StatusFilterEnum.IS_VERIFIED).setValue(false, {emitEvent: false});
          this.singleToggleGrpValueChanges[StatusFilterEnum.IS_VERIFIED] = true;
          break;
        default:
          break;
      }
    });

    this.updateFilter();
  }

  override doesFilterPass(params: IDoesFilterPassParams<Location>): boolean {
    if (!this.isFilterActive()) {
      return true; // early return, filter is inactive
    }

    const filterCb = (filterValues: string[], location: Location) => {
      let passSingleValueFilters = false;
      let passBusinessStatusFilters = false;
      let passManagementStatusFilters = false;

      const businessStatusFilters: string[] = getEnumValues(BusinessStatusListEnum);
      const managementStatusFilters: string[] = getEnumValues(ManagementStatusListEnum);

      const activeSingleValueFilters = []
      const activeBusinessStatusFilters = [];
      const activeManagementStatusFilters = [];


      filterValues.forEach(filterValue => {
        if (businessStatusFilters.includes(filterValue)) {
          activeBusinessStatusFilters.push(filterValue);
        } else if (managementStatusFilters.includes(filterValue)) {
          activeManagementStatusFilters.push(filterValue);
        } else {
          activeSingleValueFilters.push(filterValue);
        }
      });


      if (activeBusinessStatusFilters.length) {
        for (let i = 0; i < activeBusinessStatusFilters.length; i++) {
          const filterValue = activeBusinessStatusFilters[i];

          passBusinessStatusFilters = location?.businessStatus === filterValue;

          if (passBusinessStatusFilters) {
            // it's enough to pass at least 1 BusinessStatusFilter!
            break;
          }
        }

        // early return - did not pass BusinessStatusFilters
        if (!passBusinessStatusFilters) {
          return false;
        }
      } else passBusinessStatusFilters = true;


      if (activeManagementStatusFilters.length) {
        for (let i = 0; i < activeManagementStatusFilters.length; i++) {
          const filterValue = activeManagementStatusFilters[i];

          passManagementStatusFilters = location?.managementStatus === filterValue;

          if (passManagementStatusFilters) {
            // it's enough to pass at least 1 ManagementStatusFilter!
            break;
          }
        }

        // early return - did not pass ManagementStatusFilters
        if (!passManagementStatusFilters) {
          return false;
        }
      } else passManagementStatusFilters = true;


      if (activeSingleValueFilters.length) {
        for (let i = 0; i < activeSingleValueFilters.length; i++) {
          const filterValue = activeSingleValueFilters[i];

          switch (filterValue) {
            case StatusFilterEnum.HAS_BLACKLISTED_FIELDS:
              passSingleValueFilters = hasBlacklistedFields(location);
              break;
            case StatusFilterEnum.HAS_NO_BLACKLISTED_FIELDS:
              passSingleValueFilters = !hasBlacklistedFields(location);
              break;
            case StatusFilterEnum.HAS_IGNORED_DIFFERENCES:
              passSingleValueFilters = hasDifferences(location, 'ignored');
              break;
            case StatusFilterEnum.HAS_NO_IGNORED_DIFFERENCES:
              passSingleValueFilters = !hasDifferences(location, 'ignored');
              break;
            case StatusFilterEnum.IS_DIFFERENT_IN_MY_BUSINESS:
              passSingleValueFilters = hasDifferences(location, 'open');
              break;
            case StatusFilterEnum.IS_NOT_DIFFERENT_IN_MY_BUSINESS:
              passSingleValueFilters = !hasDifferences(location, 'open');
              break;
            case StatusFilterEnum.IS_FOUND_IN_MY_BUSINESS:
              passSingleValueFilters = location?.foundInMyBusiness;
              break;
            case StatusFilterEnum.IS_NOT_FOUND_IN_MY_BUSINESS:
              passSingleValueFilters = !location?.foundInMyBusiness;
              break;
            case StatusFilterEnum.HAS_COMPLETED_CC:
              passSingleValueFilters = !!location?.claimAndCleanCompletionTimestamp;
              break;
            case StatusFilterEnum.HAS_NOT_COMPLETED_CC:
              passSingleValueFilters = !location?.claimAndCleanCompletionTimestamp;
              break;
            case StatusFilterEnum.HAS_UPDATE_ERROR:
              passSingleValueFilters = !!location?.lastUpdateErrorMessage;
              break;
            case StatusFilterEnum.HAS_NO_UPDATE_ERROR:
              passSingleValueFilters = !location?.lastUpdateErrorMessage;
              break;
            case StatusFilterEnum.HAS_IMPORT_ERROR:
              passSingleValueFilters = !!location?.lastImportErrorMessage;
              break;
            case + StatusFilterEnum.HAS_NO_IMPORT_ERROR:
              passSingleValueFilters = !location?.lastImportErrorMessage;
              break;
            case StatusFilterEnum.IS_VERIFIED:
              passSingleValueFilters = location?.verified;
              break;
            case StatusFilterEnum.IS_NOT_VERIFIED:
              passSingleValueFilters = !location?.verified;
              break;
            default:
              break;
          }

          // break the loop if a location did not pass a SingleValueFilter - it needs to pass all!
          if (!passSingleValueFilters) {
            break;
          }
        }
      } else passSingleValueFilters = true;


      return passSingleValueFilters && passBusinessStatusFilters && passManagementStatusFilters;
    }

    return params.data && // check if we have a row value
        filterCb(this.filterValue, params.data);
  }

  handleReset() {
    this.resetFormGroup();
    this.updateFilter();
  }
}
